import { useEffect, useRef, useState } from 'react';
import { apiJson } from '../api/client.js';

const MAX_BYTES = 20 * 1024 * 1024;

/**
 * Drag-and-drop and browse upload for a project Files & Media tab.
 * Each file is posted separately; `fields` are appended to every request.
 *
 * @param {{
 *   endpoint: string;
 *   fields?: Record<string, string | number | null | undefined>;
 *   description?: string;
 *   onUploaded?: () => void | Promise<void>;
 *   loadRecent?: () => Promise<Array<{ id: string | number; name: string; status?: string }>>;
 *   compact?: boolean;
 * }} props
 */
export function ProjectFileUploader({ endpoint, fields = {}, description, onUploaded, loadRecent, compact = false }) {
  const inputRef = useRef(null);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [uploaded, setUploaded] = useState([]);
  const loadRecentRef = useRef(loadRecent);
  loadRecentRef.current = loadRecent;
  const scope = `${endpoint}|${JSON.stringify(fields)}`;

  useEffect(() => {
    let cancelled = false;
    setUploaded([]);
    const load = loadRecentRef.current;
    if (compact || !load) return undefined;
    load()
      .then((items) => {
        if (cancelled || !Array.isArray(items)) return;
        setUploaded((current) => {
          const seen = new Set(current.map((item) => String(item.id)));
          const extra = items.filter((item) => item && !seen.has(String(item.id)));
          return extra.length ? [...current, ...extra] : current;
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [scope, compact]);

  const uploadFiles = async (fileList) => {
    const files = [...fileList].filter((file) => file && file.size >= 0);
    if (!files.length || busy) return;

    const tooLarge = files.filter((file) => file.size > MAX_BYTES);
    const accepted = files.filter((file) => file.size <= MAX_BYTES);
    const sizeError = tooLarge.length
      ? `${tooLarge.map((file) => file.name).join(', ')} ${tooLarge.length === 1 ? 'is' : 'are'} larger than 20 MB.`
      : '';
    setError(sizeError);
    if (!accepted.length) return;

    setBusy(true);
    const saved = [];
    const failures = [];
    try {
      for (let index = 0; index < accepted.length; index += 1) {
        const file = accepted[index];
        setProgress(`Uploading ${index + 1} of ${accepted.length}: ${file.name}`);
        const body = new FormData();
        body.append('file', file);
        for (const [key, value] of Object.entries(fields)) {
          if (value !== null && value !== undefined && value !== '') {
            body.append(key, String(value));
          }
        }
        try {
          const result = await apiJson(endpoint, { method: 'POST', body });
          saved.push({
            id: result.id ?? `${file.name}-${index}`,
            name: result.label || result.file_name || file.name,
            status: result.approval_status || 'uploaded',
          });
        } catch (err) {
          failures.push(`${file.name}: ${err.message}`);
        }
      }
      if (saved.length) {
        setUploaded((current) => [...saved, ...current]);
        await onUploaded?.();
      }
      if (failures.length || sizeError) {
        setError([sizeError, ...failures].filter(Boolean).join(' '));
      }
    } finally {
      setBusy(false);
      setProgress('');
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="stack" style={{ marginBottom: compact ? '0.75rem' : '1rem' }}>
      {compact ? (
        <div className="media-upload-actions">
          <button type="button" className="btn-primary" disabled={busy} onClick={() => inputRef.current?.click()}>
            Upload files
          </button>
        </div>
      ) : null}
      <label
        className={`dropzone${compact ? ' dropzone--compact' : ''}${active ? ' is-active' : ''}${busy ? ' is-busy' : ''}`}
        onDragEnter={(event) => {
          event.preventDefault();
          setActive(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setActive(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setActive(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setActive(false);
          void uploadFiles(event.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          disabled={busy}
          onChange={(event) => {
            void uploadFiles(event.target.files);
          }}
        />
        <strong>{busy ? progress || 'Uploading…' : 'Drag and drop files here'}</strong>
        <span className="muted small">{description || 'or browse to upload documents and media'}</span>
      </label>
      {error ? <p className="err small">{error}</p> : null}
      {!compact && uploaded.length ? (
        <ul className="list-plain">
          {uploaded.map((file) => (
            <li key={file.id} className="file-row">
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>{file.name}</strong>
              </span>
              <span className={`badge badge-${file.status === 'pending' ? 'warn' : file.status === 'rejected' ? 'err' : 'ok'}`}>
                {file.status === 'pending' ? 'Pending review' : file.status === 'rejected' ? 'Rejected' : 'Uploaded'}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
